import { buildPetEquipment } from '@dcc/rules/app/petEquipmentService.ts';
import type { Character } from '@dcc/rules/domain/character.ts';
import type { SessionState } from '@dcc/rules/domain/session.ts';
import type { RuntimeState } from '@dcc/rules/domain/runtime.ts';
import type { Roller } from '@dcc/rules/rules/dice.ts';
import { processTwoHourCheckpoint, processEndOfFloorCheckpoint } from '@dcc/rules/app/advancementService.ts';
import { restCharacter } from '@dcc/rules/app/healthService.ts';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { buildEffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import { deriveStatsFor } from '@dcc/rules/app/derivedStats.ts';
import { manaRegenPerRound, restoreMana } from '@dcc/rules/rules/mana.ts';
import { regeneratePetManaPerRound } from '@dcc/rules/app/petProgressionService.ts';
import type { PartyEventKind } from '../obr/sheetsApiStorage.ts';
import { tickItemBuffs } from '@dcc/rules/rules/itemPower.ts';

export interface PartyTimePlan {
  ok: true;
  character?: Character;
  session?: SessionState;
  runtime: RuntimeState;
  label: string;
  note: string;
  advancementLines: string[];
}

export type PartyTimeOutcome = PartyTimePlan | { ok: false; reason: string };

export const PARTY_EVENT_LABELS: Record<PartyEventKind, string> = {
  'pass-two-hours': '2-Hour Play Checkpoint',
  'rest-mend-hour': '1 hour of mending',
  'rest-short': 'Short Rest (2 hours)',
  'rest-long': 'Long Rest (8 hours)',
  'rest-full-day': "Full Day's Rest (30 hours)",
  'combat-round': 'Combat Round Complete',
  'end-of-floor': 'End-of-Floor Skill Checkpoint',
};

/**
 * Converts one GM event into engine-owned Character/Session/Runtime state.
 *
 * The explicit `pass-two-hours` event is the table-play checkpoint. It represents
 * two hours of real table play for advancement only; it does NOT advance the
 * crawler's fictional clock. Rest/mending events are the separate in-game clock.
 */
export function planPartyTimeEvent(
  character: Character,
  session: SessionState,
  runtime: RuntimeState,
  kind: PartyEventKind,
  roller: Roller,
  now: string,
): PartyTimeOutcome {
  // The shared Owlbear combat tracker is currently separate from the crawler's
  // local Runtime encounter state. Complete Round therefore delivers one durable
  // event per participating crawler and applies ONLY the standing Mana house rule
  // here. The same receipt/version machinery used by rests makes the event
  // idempotent across reconnects and multiple open browser tabs.
  if (kind === 'combat-round') {
    const effective = buildEffectiveCharacter(
      character,
      activeEffects(runtime),
      (id) => contentRegistry.getItem(id),
    );
    const derived = deriveStatsFor(effective);
    const regen = manaRegenPerRound(effective.statMod('int'));
    const restored = restoreMana(derived.mana, regen);
    const crawlerChanged = restored.current !== character.currentMana;
    const regeneratedCompanions = character.companions.map((pet) => regeneratePetManaPerRound(pet, now, buildPetEquipment(character, pet, (id) => contentRegistry.getItem(id))));
    const petsChanged = regeneratedCompanions.some((pet, index) => pet !== character.companions[index]);
    const delta = restored.current - character.currentMana;
    const petDeltas = regeneratedCompanions
      .map((pet, index) => ({ name: pet.name, delta: pet.currentMana - character.companions[index]!.currentMana }))
      .filter((entry) => entry.delta > 0);
    const crawlerNote = delta > 0
      ? `${character.name}: Mana regenerated by ${delta} (${restored.current}/${derived.mana.max}) at round end.`
      : delta < 0
        ? `${character.name}: Max Mana is ${derived.mana.max}; Current Mana was capped to ${restored.current} at round end.`
        : `${character.name}: Mana already full (${restored.current}/${derived.mana.max}); round-end regeneration applied.`;
    const petNote = petDeltas.length > 0
      ? ` Pet Mana: ${petDeltas.map((entry) => `${entry.name} +${entry.delta}`).join(', ')}.`
      : '';
    // The round also passes for Buffs from Item Powers (and only those).
    const buffs = tickItemBuffs(runtime);
    const buffNote = buffs.ended.length > 0 ? ` ${buffs.ended.join(', ')} ended.` : '';
    const note = `${crawlerNote}${petNote}${buffNote}`;
    return {
      ok: true,
      ...(crawlerChanged || petsChanged ? {
        character: {
          ...character,
          currentMana: restored.current,
          companions: regeneratedCompanions,
        },
      } : {}),
      runtime: buffs.runtime,
      label: PARTY_EVENT_LABELS[kind],
      note,
      advancementLines: [],
    };
  }

  if (runtime.encounter !== null) {
    return { ok: false, reason: `${character.name}: ${PARTY_EVENT_LABELS[kind]} cannot be applied while this crawler is in an encounter.` };
  }

  if (kind === 'end-of-floor') {
    const out = processEndOfFloorCheckpoint(roller, character, session, contentRegistry, now);
    return { ok: true, character: out.character, session: out.session, runtime, label: PARTY_EVENT_LABELS[kind], note: `${character.name}: ${PARTY_EVENT_LABELS[kind]} applied. ${out.lines.join(' ') || 'No marked Rank 5+ Skills required advancement checks.'}`, advancementLines: out.lines };
  }

  if (kind === 'pass-two-hours') {
    const advancement = processTwoHourCheckpoint(roller, character, session, contentRegistry, now);
    if (!advancement.ok) return advancement;

    const award = advancement.pendingAwardCandidates.length > 0
      ? ` Tutorial-floor untrained award waiting: choose 1 of ${advancement.pendingAwardCandidates.length} Skill${advancement.pendingAwardCandidates.length === 1 ? '' : 's'}.`
      : '';
    const rolls = advancement.lines.length > 0
      ? ` ${advancement.lines.join(' ')}`
      : ' No marked Rank 1-4 Skills required advancement checks.';
    return {
      ok: true,
      character: advancement.character,
      session: advancement.session,
      // Advancement time is table-play time, not fictional elapsed time.
      runtime,
      label: PARTY_EVENT_LABELS[kind],
      note: `${character.name}: ${PARTY_EVENT_LABELS[kind]} applied. Level +${advancement.levelsGained}.${rolls}${award}`,
      advancementLines: advancement.lines,
    };
  }

  const restKind = kind === 'rest-mend-hour' ? 'mend-hour'
    : kind === 'rest-short' ? 'short'
      : kind === 'rest-long' ? 'long'
        : 'full-day';
  const outcome = restCharacter(
    character,
    restKind,
    runtime,
    activeEffects(runtime),
    (id) => contentRegistry.getItem(id),
    now,
  );
  if (!outcome.runtime) return { ok: false, reason: 'The crawler runtime could not be advanced.' };
  return {
    ok: true,
    character: outcome.character,
    runtime: outcome.runtime,
    label: PARTY_EVENT_LABELS[kind],
    note: timeNote(character.name, PARTY_EVENT_LABELS[kind], outcome.expiredEffectIds.length, outcome.endedCooldownIds.length),
    advancementLines: [],
  };
}

function timeNote(name: string, label: string, expiredEffects: number, endedCooldowns: number): string {
  const extra: string[] = [];
  if (expiredEffects > 0) extra.push(`${expiredEffects} condition${expiredEffects === 1 ? '' : 's'} ended`);
  if (endedCooldowns > 0) extra.push(`${endedCooldowns} cooldown${endedCooldowns === 1 ? '' : 's'} finished`);
  return extra.length > 0 ? `${name}: ${label} applied; ${extra.join(', ')}.` : `${name}: ${label} applied.`;
}
